import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AuthService } from '../../services/auth.service';
import { PASSWORD_CHANGED_NOTICE_KEY, SESSION_REVOKED_NOTICE_KEY } from '../../constants/app.constants';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.less'
})
export class ChangePasswordComponent implements OnInit {
  updatePasswordForm: FormGroup;
  passwordVisible = false;
  passwordStrength: 'weak' | 'medium' | 'strong' | '' = '';
  hasLocalPassword = true;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private message: NzMessageService,
    private translate: TranslateService,
  ) {
    this.updatePasswordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/),
        this.passwordByteLengthValidator,
      ]],
      confirmPassword: ['', Validators.required],
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.updatePasswordForm.get('newPassword')?.valueChanges.subscribe(value => {
      this.passwordStrength = this.evaluatePasswordStrength(value);
    });

    this.updatePasswordForm.get('currentPassword')?.valueChanges.subscribe(() => {
      this.clearCurrentPasswordInvalidError();
    });

    this.authService.getMe().subscribe({
      next: (profile) => {
        this.hasLocalPassword = profile?.hasLocalPassword === true;
      },
      error: () => {
        // Preserve the existing form when the profile status cannot be loaded.
      }
    });
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  passwordMatchValidator(form: AbstractControl) {
    const group = form as FormGroup;
    return group.controls['newPassword']?.value === group.controls['confirmPassword']?.value
      ? null
      : { mismatch: true };
  }

  onUpdatePasswordSubmit(): void {
    if (this.updatePasswordForm.valid) {
      const { currentPassword, newPassword } = this.updatePasswordForm.value;
      const token = this.authService.getToken();

      if (token) {
        this.authService.updatePassword(currentPassword, newPassword).subscribe({
          next: () => {
            sessionStorage.removeItem(SESSION_REVOKED_NOTICE_KEY);
            sessionStorage.setItem(PASSWORD_CHANGED_NOTICE_KEY, '1');
            this.authService.logout(true);
          },
          error: (err) => {
            const errorCode = err?.error?.code || err?.code;
            if (errorCode === 'PASSWORD_CHANGE_RATE_LIMITED') {
              this.message.error(this.translate.instant('LOGIN.PASSWORD_CHANGE_RATE_LIMITED'));
              return;
            }
            if (errorCode === 'CURRENT_PASSWORD_INVALID') {
              const currentPassword = this.updatePasswordForm.get('currentPassword');
              if (currentPassword) {
                currentPassword.setErrors({
                  ...(currentPassword.errors || {}),
                  currentPasswordInvalid: true,
                });
                currentPassword.markAsTouched();
              }
              this.message.error(this.translate.instant('LOGIN.CURRENT_PASSWORD_INVALID'));
              return;
            }
            if (errorCode === 'LOCAL_PASSWORD_NOT_SET') {
              this.hasLocalPassword = false;
              return;
            }
            this.message.error(this.translate.instant('LOGIN.PASSWORD_UPDATE_FAILED'));
            console.error(err);
          }
        });
      } else {
        this.message.error(this.translate.instant('LOGIN.RESET_TOKEN_MISSING'));
      }
      return;
    }

    this.updatePasswordForm.markAllAsTouched();

    if (this.updatePasswordForm.get('newPassword')?.errors?.['minlength']) {
      this.message.error(this.translate.instant('LOGIN.PASSWORD_TOO_SHORT'));
    }
    if (this.updatePasswordForm.get('newPassword')?.errors?.['pattern']) {
      this.message.error(this.translate.instant('LOGIN.PASSWORD_WEAK'));
    }
    if (this.updatePasswordForm.get('newPassword')?.errors?.['passwordTooLong']) {
      this.message.error(this.translate.instant('LOGIN.PASSWORD_TOO_LONG'));
    }
    if (this.updatePasswordForm.getError('mismatch')) {
      this.message.error(this.translate.instant('LOGIN.PASSWORDS_DO_NOT_MATCH'));
    }
  }

  evaluatePasswordStrength(password: string): 'weak' | 'medium' | 'strong' | '' {
    if (!password) return '';

    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /\W/.test(password);

    const score = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (password.length >= 8 && score >= 4) return 'strong';
    if (password.length >= 6 && score >= 2) return 'medium';
    return 'weak';
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.updatePasswordForm.get(controlName);
    return Boolean(control?.invalid && (control.touched || control.dirty));
  }

  isConfirmPasswordInvalid(): boolean {
    const confirmPassword = this.updatePasswordForm.get('confirmPassword');
    return Boolean(
      (confirmPassword?.invalid || this.updatePasswordForm.hasError('mismatch'))
      && (confirmPassword?.touched || confirmPassword?.dirty)
    );
  }

  private clearCurrentPasswordInvalidError(): void {
    const currentPassword = this.updatePasswordForm.get('currentPassword');
    const errors = currentPassword?.errors;
    if (!currentPassword || !errors?.['currentPasswordInvalid']) {
      return;
    }

    const remainingErrors = { ...errors };
    delete remainingErrors['currentPasswordInvalid'];
    currentPassword.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private passwordByteLengthValidator(control: AbstractControl): { passwordTooLong: true } | null {
    const password = typeof control.value === 'string' ? control.value : '';
    return new TextEncoder().encode(password).length > 72 ? { passwordTooLong: true } : null;
  }
}
